import { HeadingLevel } from "@ariakit/react";
import {
  Failure,
  MinWidth,
  Pending,
  Success,
  useMediaQuery,
} from "@indietabletop/appkit";
import type { ReactNode } from "react";
import { Link, Route, Switch, useParams } from "wouter";
import type { Gang } from "../../engine/Gang.ts";
import { FailureView, type KnownFailure } from "../failures/FailureView.tsx";
import { NotFoundPage } from "../failures/NotFoundPage.tsx";
import { CloseIcon } from "../icons/CloseIcon.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import {
  HeaderBackButton,
  HeaderTitle,
  Page,
  PageHeader,
} from "../Page/Page.tsx";
import brawl from "./brawl.webp";
import { GangActionsMenu } from "./GangActionsMenu.tsx";
import { GangView } from "./GangView.tsx";
import { GitActionsMenu } from "./GitActionsMenu.tsx";
import { GitView } from "./GitView.tsx";
import { page, subview } from "./style.css.ts";
import { GangContext, useGang, useSharedGang } from "./useGang.ts";
import { useGit } from "./useGit.tsx";
import { useVehicle } from "./useVehicle.tsx";
import { VehicleActionsMenu } from "./VehicleActionsMenu.tsx";
import { VehicleView } from "./VehicleView.tsx";

function DesktopViewHeader(props: {
  backHref: string;
  overline: string;
  heading: string;
  actions: ReactNode;
}) {
  const { backHref, overline, heading, actions } = props;
  return (
    <div className={subview.header}>
      <Link href={backHref}>
        <CloseIcon />
      </Link>

      <div className={subview.middleSection}>
        <div className={subview.overline}>{overline}</div>
        <div className={subview.heading}>{heading}</div>
      </div>

      <div>{actions}</div>
    </div>
  );
}

function GitHeader() {
  const { git } = useGit();

  const heading = git?.isDead
    ? "Death Certificate"
    : git?.isGnawball
      ? "Pet Passport"
      : git?.isRunt
        ? "Birth Certificate"
        : "Identity Card";

  return (
    <DesktopViewHeader
      backHref="/"
      overline="City of Gnork"
      heading={heading}
      actions={<GitActionsMenu />}
    />
  );
}

function VehicleHeader() {
  const { vehicle } = useVehicle();

  const newLocal = vehicle?.isWriteOff
    ? "Crash Report"
    : vehicle?.isBuzzcopter
      ? "Flying License"
      : "Driving License";

  return (
    <DesktopViewHeader
      backHref="/"
      overline="City of Gnork"
      heading={newLocal}
      actions={<VehicleActionsMenu />}
    />
  );
}

function DesktopView(props: { children: ReactNode }) {
  const { children } = props;

  return (
    <div className={subview.content}>
      <div className={page.desktopView.content}>{children}</div>
    </div>
  );
}

function GangResultView(props: {
  result: Pending | Success<Gang> | Failure<KnownFailure>;
  readOnly?: boolean;
}) {
  const { result, readOnly } = props;
  const isWide = useMediaQuery(MinWidth.WIDE);

  if (result.isPending) {
    return (
      <Page title="" backHref="~/" centerChildren>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page title="" backHref="~/" centerChildren>
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const context = { gang: result.value, readOnly };

  if (isWide) {
    return (
      <GangContext value={context}>
        <HeadingLevel>
          <div className={page.splitContainer}>
            <div className={page.gangView}>
              <PageHeader>
                <HeaderBackButton backHref="~/gangs" />
                <HeaderTitle>Gang</HeaderTitle>
                <GangActionsMenu />
              </PageHeader>

              <div className={page.gangContent}>
                <GangView />
              </div>
            </div>

            <div className={page.gitView}>
              <HeadingLevel>
                <Switch>
                  <Route path="/g/:gitId">
                    <DesktopView>
                      <GitHeader />
                      <GitView />
                    </DesktopView>
                  </Route>

                  <Route path="/v/:vehicleId">
                    <DesktopView>
                      <VehicleHeader />
                      <VehicleView />
                    </DesktopView>
                  </Route>

                  <Route>
                    <div
                      style={{
                        display: "flex",
                        placeItems: "center",
                        backgroundColor: "hsl(0 0% 0% / 0.05)",
                      }}
                    >
                      <img
                        src={brawl}
                        alt=""
                        style={{
                          maxInlineSize: "95%",
                          blockSize: "auto",
                          maxBlockSize: "80%",
                          margin: "auto",
                          paddingBlockEnd: "4rem",
                        }}
                      />
                    </div>
                  </Route>
                </Switch>
              </HeadingLevel>
            </div>
          </div>
        </HeadingLevel>
      </GangContext>
    );
  }

  return (
    <GangContext value={context}>
      <Switch>
        <Route path="/">
          <Page
            backHref="~/gangs"
            title="Gang"
            actions={<GangActionsMenu />}
            withMobilePadding={false}
          >
            <div className={page.gangMobileView}>
              <GangView />
            </div>
          </Page>
        </Route>

        <Route path="/g/:gitId">
          <Page
            backHref="/"
            title="Git"
            actions={<GitActionsMenu />}
            withMobilePadding={false}
          >
            <div className={page.gitMobileView}>
              <GitView />
            </div>
          </Page>
        </Route>

        <Route path="/v/:vehicleId">
          <Page
            backHref="/"
            title="Vehicle"
            actions={<VehicleActionsMenu />}
            withMobilePadding={false}
          >
            <div className={page.gitMobileView}>
              <VehicleView />
            </div>
          </Page>
        </Route>

        <Route>
          <NotFoundPage />
        </Route>
      </Switch>
    </GangContext>
  );
}

export function GangPage() {
  const { gangId } = useParams<{ gangId: string }>();
  const result = useGang(gangId);
  return <GangResultView result={result} />;
}

export function SharedGangPage() {
  const { gangId } = useParams<{ gangId: string }>();
  const result = useSharedGang(gangId);
  return <GangResultView result={result} readOnly />;
}
